import { useMemo, useState } from 'react';
import type { ReportBiomarker, Status, BiomarkerKind } from '@/lib/types';

type StatusFilter = 'all' | Status;
type KindFilter = 'all' | BiomarkerKind;
type ViewMode = 'list' | 'grid';

const statusBadge: Record<Status, string> = {
  ok: 'bg-ok-tint text-ok-deep',
  warn: 'bg-warn-tint text-warn-deep',
  crit: 'bg-crit-tint text-crit-deep',
};

const statusLabel: Record<Status, string> = {
  ok: 'Óptimo',
  warn: 'Atención',
  crit: 'Crítico',
};

const statusValueColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

interface BiomarkersTabProps {
  biomarkers: ReportBiomarker[];
}

export function BiomarkersTab({ biomarkers }: BiomarkersTabProps) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [view, setView] = useState<ViewMode>('list');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return biomarkers.filter((b) => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (kindFilter !== 'all' && b.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        b.name.toLowerCase().includes(q) ||
        b.code.toLowerCase().includes(q) ||
        b.category.toLowerCase().includes(q)
      );
    });
  }, [biomarkers, query, statusFilter, kindFilter]);

  const counts = useMemo(() => {
    return biomarkers.reduce(
      (acc, b) => {
        acc.all += 1;
        acc[b.status] += 1;
        return acc;
      },
      { all: 0, ok: 0, warn: 0, crit: 0 } as Record<StatusFilter, number>
    );
  }, [biomarkers]);

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="flex-1 min-w-[240px] bg-glass backdrop-blur-md border border-line-glass rounded-md shadow-recessed px-3 flex items-center gap-2.5 h-[36px] transition-all focus-within:bg-surface focus-within:border-line-default">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, código o categoría…"
            className="flex-1 min-w-0 border-0 outline-0 bg-transparent text-[13px] placeholder:text-faint"
          />
        </div>
        <SegmentedControl
          options={[
            { value: 'all', label: 'Todos', count: counts.all },
            { value: 'crit', label: 'Críticos', count: counts.crit },
            { value: 'warn', label: 'Atención', count: counts.warn },
            { value: 'ok', label: 'Óptimos', count: counts.ok },
          ]}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
        />
        <SegmentedControl
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'core', label: 'Core' },
            { value: 'derived', label: 'Derivados' },
          ]}
          value={kindFilter}
          onChange={(v) => setKindFilter(v as KindFilter)}
        />
        <SegmentedControl
          options={[
            { value: 'list', label: 'Lista' },
            { value: 'grid', label: 'Cards' },
          ]}
          value={view}
          onChange={(v) => setView(v as ViewMode)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="glass-panel py-10 text-center text-muted text-[13px]">
          Ningún biomarcador coincide con los filtros.
        </div>
      ) : view === 'list' ? (
        <div className="glass-panel">
          <div
            aria-hidden="true"
            className="grid grid-cols-[1fr_auto_120px_120px_100px] gap-4 items-center px-5 py-2.5 text-[10px] uppercase tracking-[0.06em] font-semibold text-muted border-b border-line-glass max-[760px]:hidden"
          >
            <span>Biomarcador</span>
            <span>Tipo</span>
            <span className="text-right">Valor</span>
            <span>Rango óptimo</span>
            <span className="text-right">Status</span>
          </div>
          {filtered.map((b) => (
            <div
              key={b.code}
              className="grid grid-cols-[1fr_auto_120px_120px_100px] gap-4 items-center px-5 py-3 border-b border-line-glass last:border-b-0 max-[760px]:grid-cols-[1fr_auto] max-[760px]:gap-x-3 max-[760px]:gap-y-1"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-semibold tracking-[-0.005em] truncate">
                  {b.name}
                </div>
                <div className="text-[11px] text-tertiary mt-px tnum">
                  {b.code} · {b.category}
                </div>
              </div>
              <span className="text-[10px] text-tertiary uppercase tracking-[0.04em] font-medium max-[760px]:hidden">
                {b.kind === 'core' ? 'Core' : 'Derivado'}
              </span>
              <div className={`text-right text-[14px] font-semibold tnum ${statusValueColor[b.status]} max-[760px]:col-start-2 max-[760px]:row-start-1 max-[760px]:row-span-2`}>
                {b.value}
                {b.unit && <span className="text-[11px] text-tertiary font-normal ml-0.5">{b.unit}</span>}
              </div>
              <div className="text-[12px] text-tertiary tnum max-[760px]:hidden">
                {b.range}
              </div>
              <div className="text-right max-[760px]:hidden">
                <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.04em] uppercase px-2 py-0.5 rounded-sm ${statusBadge[b.status]}`}>
                  {statusLabel[b.status]}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2.5 max-[1024px]:grid-cols-2 max-[760px]:grid-cols-1">
          {filtered.map((b) => (
            <div key={b.code} className="glass-panel p-4 px-[18px] flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-tertiary uppercase tracking-[0.04em] font-medium tnum">
                  {b.code}
                </span>
                <span className={`text-[10px] font-semibold tracking-[0.04em] uppercase px-2 py-0.5 rounded-sm ${statusBadge[b.status]}`}>
                  {statusLabel[b.status]}
                </span>
              </div>
              <div className="text-[13px] font-semibold tracking-[-0.005em]">{b.name}</div>
              <div className={`text-[24px] font-bold tracking-[-0.025em] tnum ${statusValueColor[b.status]} mt-1`}>
                {b.value}
                {b.unit && <span className="text-[12px] text-tertiary font-medium ml-1">{b.unit}</span>}
              </div>
              <RangeBar
                position={b.barPosition}
                status={b.status}
                optimalStart={b.optimalStart}
                optimalEnd={b.optimalEnd}
              />
              <div className="text-[11px] text-tertiary tnum">
                {b.category} · rango {b.range}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface SegmentOption {
  value: string;
  label: string;
  count?: number;
}

interface SegmentedControlProps {
  options: SegmentOption[];
  value: string;
  onChange: (next: string) => void;
}

function SegmentedControl({ options, value, onChange }: SegmentedControlProps) {
  return (
    <div className="glass-panel flex p-0.5 gap-0.5">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-2.5 py-1 rounded-[6px] text-[12px] font-medium transition-all cursor-pointer outline-none ${
              active
                ? 'bg-surface text-primary shadow-pill'
                : 'bg-transparent text-tertiary hover:text-primary'
            }`}
          >
            {o.label}
            {typeof o.count === 'number' && (
              <span className={`ml-1 text-[10px] tnum ${active ? 'text-tertiary' : 'text-faint'}`}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface RangeBarProps {
  position: number;
  status: Status;
  optimalStart: number;
  optimalEnd: number;
}

const markerBg: Record<Status, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
};

function RangeBar({ position, status, optimalStart, optimalEnd }: RangeBarProps) {
  // The translucent band represents the optimal zone for THIS biomarker
  // (bilateral ranges sit centered, "< X" types start at 0, "> X" end at 100).
  // The colored marker shows where the value falls on the 0-100 scale.
  const clamped = Math.max(2, Math.min(98, position));
  return (
    <div className="relative h-1 bg-soft rounded-full mt-1.5 mb-0.5" aria-hidden="true">
      <div
        className="absolute inset-y-0 bg-glass border border-line-glass rounded-full"
        style={{ left: `${optimalStart}%`, right: `${100 - optimalEnd}%` }}
      />
      <span
        className={`absolute top-1/2 w-[3px] h-3 -translate-x-1/2 -translate-y-1/2 rounded-[2px] ${markerBg[status]}`}
        style={{ left: `${clamped}%` }}
      />
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" className="text-faint" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-faint" />
    </svg>
  );
}
